/**
 * The clarification contracts accept JSON.parse products, never live in-process objects.
 * This is the one parse boundary: all nested values are copied from own data descriptors
 * before any validator or projector can inspect them.
 */
export type ClarificationTrustedData =
  | null
  | string
  | number
  | boolean
  | symbol
  | readonly ClarificationTrustedData[]
  | ClarificationTrustedRecord;

export interface ClarificationTrustedRecord {
  readonly [key: string]: ClarificationTrustedData;
}

export type ClarificationTrustedParseResult =
  | Readonly<{ ok: true; value: ClarificationTrustedData }>
  | Readonly<{ ok: false }>;

type TrustedParseOptions = Readonly<{
  allowNativeDate?: boolean;
  allowSymbols?: boolean;
}>;

type ParseState = {
  nodeCount: number;
  readonly options: TrustedParseOptions;
};

const MAX_TRUSTED_PARSE_DEPTH = 32;
const MAX_TRUSTED_PARSE_NODES = 4_096;

/**
 * Copy JSON-shaped values through reflection intrinsics only. A rejected traversal never
 * returns a partial result; callers translate the typed rejection to their closed reason.
 */
export function parseClarificationTrustedData(
  input: unknown,
  options: TrustedParseOptions = {}
): ClarificationTrustedParseResult {
  return parseValue(input, { nodeCount: 0, options }, 0);
}

export function isClarificationTrustedRecord(
  value: ClarificationTrustedData
): value is ClarificationTrustedRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isClarificationTrustedArray(
  value: ClarificationTrustedData
): value is readonly ClarificationTrustedData[] {
  return Array.isArray(value);
}

function parseValue(
  value: unknown,
  state: ParseState,
  depth: number
): ClarificationTrustedParseResult {
  if (depth > MAX_TRUSTED_PARSE_DEPTH || state.nodeCount >= MAX_TRUSTED_PARSE_NODES) {
    return rejected();
  }
  state.nodeCount += 1;
  if (value === null) return accepted(null);
  if (typeof value === 'number') {
    return Number.isFinite(value) ? accepted(value) : rejected();
  }
  if (typeof value === 'string' || typeof value === 'boolean') {
    return accepted(value);
  }
  if (typeof value === 'symbol') {
    return state.options.allowSymbols ? accepted(value) : rejected();
  }
  if (typeof value !== 'object') return rejected();

  if (state.options.allowNativeDate) {
    const nativeDateTime = readNativeDateTime(value);
    if (nativeDateTime !== null) return accepted(nativeDateTime);
  }

  const objectValue = value as object;
  const shape = readObjectShape(objectValue);
  if (shape === 'array') return parseArray(objectValue, state, depth + 1);
  if (shape === 'record') return parseRecord(objectValue, state, depth + 1);
  return rejected();
}

function parseArray(
  value: object,
  state: ParseState,
  depth: number
): ClarificationTrustedParseResult {
  const descriptors = readOwnDataDescriptors(value);
  if (descriptors === null) return rejected();
  const lengthDescriptor = descriptors.get('length');
  if (lengthDescriptor === undefined || lengthDescriptor.enumerable !== false) return rejected();
  const length = lengthDescriptor.value;
  if (!isArrayLength(length) || descriptors.size !== length + 1) return rejected();
  if (length > MAX_TRUSTED_PARSE_NODES - state.nodeCount) return rejected();

  const copied: ClarificationTrustedData[] = [];
  for (let index = 0; index < length; index += 1) {
    const descriptor = descriptors.get(String(index));
    if (descriptor === undefined || descriptor.enumerable !== true) return rejected();
    const parsed = parseValue(descriptor.value, state, depth);
    if (!parsed.ok) return parsed;
    copied.push(parsed.value);
  }
  return accepted(copied);
}

function parseRecord(
  value: object,
  state: ParseState,
  depth: number
): ClarificationTrustedParseResult {
  const descriptors = readOwnDataDescriptors(value);
  if (descriptors === null) return rejected();
  const copied: Record<string, ClarificationTrustedData> = {};
  for (const [key, descriptor] of descriptors) {
    if (key === '__proto__' || descriptor.enumerable !== true) return rejected();
    const parsed = parseValue(descriptor.value, state, depth);
    if (!parsed.ok) return parsed;
    copied[key] = parsed.value;
  }
  return accepted(copied);
}

function readObjectShape(value: object): 'array' | 'record' | null {
  try {
    if (Array.isArray(value)) {
      return Object.getPrototypeOf(value) === Array.prototype ? 'array' : null;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null ? 'record' : null;
  } catch {
    return null;
  }
}

function readOwnDataDescriptors(value: object): ReadonlyMap<string, PropertyDescriptor> | null {
  let keys: readonly PropertyKey[];
  try {
    keys = [
      ...Object.getOwnPropertyNames(value),
      ...Object.getOwnPropertySymbols(value),
    ];
  } catch {
    return null;
  }
  const descriptors = new Map<string, PropertyDescriptor>();
  for (const key of keys) {
    if (typeof key !== 'string') return null;
    const descriptor = readDataDescriptor(value, key);
    if (descriptor === null || descriptors.has(key)) return null;
    descriptors.set(key, descriptor);
  }
  return descriptors;
}

function readDataDescriptor(value: object, key: string): PropertyDescriptor | null {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined) return null;
    if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) return null;
    if (
      Object.prototype.hasOwnProperty.call(descriptor, 'get') ||
      Object.prototype.hasOwnProperty.call(descriptor, 'set')
    ) {
      return null;
    }
    return descriptor;
  } catch {
    return null;
  }
}

function readNativeDateTime(value: object): number | null {
  try {
    if (Object.getPrototypeOf(value) !== Date.prototype) return null;
    const milliseconds = Date.prototype.getTime.call(value);
    return Number.isFinite(milliseconds) ? milliseconds : null;
  } catch {
    return null;
  }
}

function isArrayLength(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function accepted(value: ClarificationTrustedData): ClarificationTrustedParseResult {
  return { ok: true, value };
}

function rejected(): ClarificationTrustedParseResult {
  return { ok: false };
}
